import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import type { RouteObject } from "react-router-dom";
import { App, BOOT_ROUTES } from "./App";
import { applyInitialTheme } from "./theme";
import "@fontsource/figtree/400.css";
import "@fontsource/figtree/500.css";
import "@fontsource/figtree/600.css";
import "@fontsource/figtree/700.css";
import "@fontsource/geist-mono/400.css";
import "@fontsource/geist-mono/500.css";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/components.css";

/**
 * The 06a → 06b seam.
 *
 * §D0 gives `src/main.tsx` to task 06a and `src/router.tsx` to task 06b, and "no two tasks write the same file".
 * A plain `import { routes } from "./router"` would therefore make 06a's own `npm run typecheck` fail on a module
 * only 06b is allowed to create. `import.meta.glob` is Vite's own module discovery: a pattern that matches
 * nothing returns `{}` with no type error and no runtime error, and the moment 06b lands `router.tsx` the real
 * routes are picked up with no edit to this file. The generic is the contract 06b's `router.tsx` must satisfy:
 * it exports `routes: RouteObject[]`.
 */
const routerModules = import.meta.glob<{ routes: RouteObject[] }>("./router.tsx", { eager: true });
const discovered = Object.values(routerModules)[0];
const routes: RouteObject[] = discovered?.routes ?? BOOT_ROUTES;

const container = document.getElementById("root");
if (container === null) {
  throw new Error("#root is missing from index.html");
}

applyInitialTheme();

createRoot(container).render(
  <StrictMode>
    <App routes={routes} />
  </StrictMode>,
);
